import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { HighlightSegment, Finding } from '@/api/domains/analyses/types';
import styles from './ComparisonColumn.module.css';
import { HighlightBlock } from '../';
import { makeAnchorId } from '@/features/analyses/pages/AnalysisDetail/utils';

interface ComparisonColumnProps {
  finding: Finding;
  documentName: string;
  documentSource: string;
  sectionId: string;
  isDoc1: boolean;
}

export const ComparisonColumn = ({
  finding,
  documentName,
  documentSource,
  sectionId,
  isDoc1
}: ComparisonColumnProps) => {
  const highlights = finding.rag_insight?.highlights;

  let relevantSegments: HighlightSegment[] = [];
  let emptyStateReason: string | null = null;
  let columnLabel: string = '';

  if (finding.finding_type === 'GAP') {
    if (isDoc1) {
      relevantSegments = highlights?.unique_in_doc1 || [];
      columnLabel = 'Missing Requirement (from External Document)';
    } else {
      emptyStateReason = 'Requirement not found in internal document';
      columnLabel = 'Not Present';
    }
  } else if (finding.finding_type === 'SUPPLEMENT') {
    if (isDoc1) {
      emptyStateReason = 'Content not present in external document';
      columnLabel = 'Not Present';
    } else {
      relevantSegments = highlights?.unique_in_doc2 || [];
      columnLabel = 'Additional Content (in Internal Document)';
    }
  } else if (finding.finding_type === 'CONTRADICTION') {
    if (isDoc1) {
      relevantSegments = highlights?.unique_in_doc1 || [];
      columnLabel = 'External Requirement (Contradicts ➜)';
    } else {
      relevantSegments = highlights?.unique_in_doc2 || [];
      columnLabel = 'Internal Rule (Contradicts ⬅︎)';
    }
  } else if (finding.finding_type === 'CONFLICT') {
    if (isDoc1) {
      relevantSegments = highlights?.unique_in_doc1 || [];
      columnLabel = 'Content in External Document';
    } else {
      relevantSegments = highlights?.unique_in_doc2 || [];
      columnLabel = 'Content in Internal Document';
    }
    if (relevantSegments.length === 0) {
      relevantSegments = highlights?.matching_segments || [];
      columnLabel = isDoc1 ? 'External Document' : 'Internal Document';
    }
  } else {
    relevantSegments = highlights?.matching_segments || [];
    columnLabel = isDoc1 ? 'External Document' : 'Internal Document';
  }

  const displaySectionId = sectionId !== 'N/A' && sectionId ? sectionId : null;
  const anchorId = displaySectionId ? makeAnchorId(displaySectionId) : null;
  const sectionJumpUrl = `${
    window.location.origin
  }/documents/${encodeURIComponent(documentSource)}${anchorId ? `#${anchorId}` : ''}`;

  const keyPrefix = `${finding.finding_id}-${isDoc1 ? 'doc1' : 'doc2'}`;

  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>
        <div className={styles.columnTitleRow}>
          <h4 className={styles.columnTitle}>{documentName}</h4>
          {displaySectionId && (
            <Chip
              label={displaySectionId}
              className={styles.sectionChip}
              icon="pi pi-bookmark"
            />
          )}
        </div>
        <Button
          label="View in Document"
          icon="pi pi-external-link"
          iconPos="right"
          link
          className={styles.jumpLink}
          onClick={() => window.open(sectionJumpUrl, '_blank')}
        />
      </div>

      <div className={styles.columnLabel}>
        <i className="pi pi-tag" />
        {columnLabel}
      </div>

      <div className={styles.columnContent}>
        {emptyStateReason ? (
          <div className={styles.emptyState}>
            <i className="pi pi-info-circle" style={{ fontSize: '2rem' }} />
            <p>{emptyStateReason}</p>
          </div>
        ) : relevantSegments.length > 0 ? (
          relevantSegments.map((segment, idx) => (
            <HighlightBlock
              key={`${keyPrefix}-segment-${idx}`}
              segment={segment}
            />
          ))
        ) : (
          <div className={styles.emptyState}>
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '2rem' }}
            />
            <p>No content highlighted</p>
          </div>
        )}
      </div>
    </div>
  );
};
